import { LoadingButton } from '@mui/lab';
import { Box, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { useToaster } from 'src/utils/toaster/toasterContext';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const AddCard = ({ customReportId, fetchCustomReportDetails }) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [reportDetails, setReportDetails] = useState([]);
  const [filteredService, setFilteredService] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [addingCardId, setAddingCardId] = useState(null); // Track which card is being added
  const sideBarWidth = 500;

  useEffect(() => {
    fetchReportDetails();
  }, []);

  const fetchReportDetails = () => {
    setLoading(true);
    let getProps = {
      url: reportModuleURL.fetchReportDetails,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const successCallback = (data, message) => {
    showToast(message, 's');
    setLoading(false);
    setReportDetails(data);
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
  };

  const handleAddCard = (card) => {
    setAddingCardId(card.report_card_details.id); // Set loading state for specific card
    const postProps = {
      url: reportModuleURL.addCustomReportCard,
      body: {
        custom_report_id: customReportId,
        report_card_id: card.report_card_details.id,
        connected_profile_id: card.connected_profile_id,
        service_name: card.service_name,
      },
      successCallback: (data, message) => {
        showToast(message, 's');
        setAddingCardId(null);
        fetchCustomReportDetails();
      },
      failureCallback: (message) => {
        showToast(message, 'e');
        setAddingCardId(null);
      },
    };
    HttpServices.Post(postProps);
  };

  const filteredCards = reportDetails.filter((item) => {
    const matchService = filteredService === 'all' || item.service_name === filteredService;
    const matchSearch = item.report_card_details.card_name.toLowerCase().includes(searchText.toLowerCase());
    return matchService && matchSearch;
  });

  return (
    <StyledComponent.StyledStack sx={{ width: sideBarWidth, p: 3 }}>
      {/* Filter Section */}
      <Stack direction="row" spacing={1} mb={2}>
        <Chip
          label={translate('custom_report.all')}
          onClick={() => setFilteredService('all')}
          color={filteredService === 'all' ? 'primary' : 'default'}
        />
        <Chip
          avatar={CommonFunction.SocialMediaAccountImage('facebook')}
          label={translate('custom_report.facebook')}
          onClick={() => setFilteredService('facebook')}
          color={filteredService === 'facebook' ? 'primary' : 'default'}
        />
        <Chip
          avatar={CommonFunction.SocialMediaAccountImage('instagram')}
          label={translate('custom_report.instagram')}
          onClick={() => setFilteredService('instagram')}
          color={filteredService === 'instagram' ? 'primary' : 'default'}
        />
        <Chip
          avatar={CommonFunction.SocialMediaAccountImage('youtube')}
          label={translate('custom_report.youtube')}
          onClick={() => setFilteredService('youtube')}
          color={filteredService === 'youtube' ? 'primary' : 'default'}
        />
        {/* Add more platforms as needed */}
      </Stack>

      {/* Search */}
      <TextField
        placeholder={translate('custom_report.search')}
        fullWidth
        size="small"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Divider />

      {/* Cards List */}
      <Box sx={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', mt: 2 }}>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            {translate('custom_report.loading')}
          </Typography>
        ) : filteredCards.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            {translate('custom_report.no_report_card')}
          </Typography>
        ) : (
          filteredCards.map((item) => (
            <>
              <Stack
                key={item.report_card_details.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {CommonFunction.InitialReportAccountBadgeWithName(item, 30)}

                  <Typography fontWeight={500}>{item.profile_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.report_card_details.card_name}
                  </Typography>
                </Stack>

                <LoadingButton
                  variant="contained"
                  loading={addingCardId === item.report_card_details.id}
                  onClick={() => handleAddCard(item)}
                  sx={{ minWidth: '80px' }}
                >
                  {translate('custom_report.add')}
                </LoadingButton>
              </Stack>
              <Divider sx={{ mt: 1 }} />
            </>
          ))
        )}
      </Box>
    </StyledComponent.StyledStack>
  );
};

export default AddCard;
